/**
 * Frontend Logger Utility
 * 
 * A centralized logging system that provides:
 * - Environment-aware logging (disabled in production by default)
 * - Structured logging with metadata
 * - Consistent log formats
 * - Performance monitoring
 * - Error tracking integration ready
 * 
 * Usage:
 *   import { logger } from '@/utils/logger';
 *   logger.info('User action', { userId: 123, action: 'click' });
 *   logger.error('API Error', error, { endpoint: '/api/users' });
 */

import { getAppConfig } from './config';

export type LogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface LogMetadata {
  [key: string]: any;
}

export interface LogContext {
  timestamp: string;
  level: LogLevel;
  message: string;
  metadata?: LogMetadata;
  error?: Error;
  stack?: string;
  userAgent?: string;
  url?: string;
  component?: string;
  sessionId?: string;
}

class FrontendLogger {
  private enableDebug: boolean;
  private environment: string;
  private sessionId?: string;

  constructor() {
    try {
      const config = getAppConfig();
      this.enableDebug = config.enableDebug;
      this.environment = config.environment;
    } catch {
      // Fallback if config is not available
      this.enableDebug = process.env.NODE_ENV === 'development';
      this.environment = process.env.NODE_ENV || 'development';
    }

    // Generate a session ID for tracking user sessions
    this.sessionId = this.generateSessionId();
  }

  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private shouldLog(level: LogLevel): boolean {
    // Always log errors and warnings
    if (level === 'error' || level === 'warn') {
      return true;
    }

    // Only log info and debug in development or when debug is enabled
    return this.enableDebug || this.environment === 'development';
  }

  private formatMessage(level: LogLevel, message: string, metadata?: LogMetadata, error?: Error): LogContext {
    const context: LogContext = {
      timestamp: new Date().toISOString(),
      level,
      message,
      userAgent: navigator.userAgent,
      url: window.location.href,
      sessionId: this.sessionId,
    };

    if (metadata) {
      context.metadata = metadata;
    }

    if (error) {
      context.error = error;
      context.stack = error.stack;
    }

    return context;
  }

  private logToConsole(context: LogContext): void {
    const { level, message, metadata, error } = context;
    
    // Create a clean log prefix
    const prefix = `[${context.timestamp.split('T')[1].split('.')[0]}][${level.toUpperCase()}]`;
    
    // Choose appropriate console method
    const consoleMethod = level === 'error' ? 'error' : 
                         level === 'warn' ? 'warn' : 
                         level === 'debug' ? 'debug' : 'log';

    // eslint-disable-next-line no-console
    if (error && metadata) {
      // eslint-disable-next-line no-console
      console[consoleMethod](prefix, message, metadata, error);
    } else if (error) {
      // eslint-disable-next-line no-console
      console[consoleMethod](prefix, message, error);
    } else if (metadata) {
      // eslint-disable-next-line no-console
      console[consoleMethod](prefix, message, metadata);
    } else {
      // eslint-disable-next-line no-console
      console[consoleMethod](prefix, message);
    }
  }

  /**
   * Log debug information - only shown in development
   */
  debug(message: string, metadata?: LogMetadata): void {
    if (!this.shouldLog('debug')) return;
    
    const context = this.formatMessage('debug', message, metadata);
    this.logToConsole(context);
  }

  /**
   * Log general information
   */
  info(message: string, metadata?: LogMetadata): void {
    if (!this.shouldLog('info')) return;
    
    const context = this.formatMessage('info', message, metadata);
    this.logToConsole(context);
  }

  /**
   * Log warnings
   */
  warn(message: string, metadata?: LogMetadata): void {
    if (!this.shouldLog('warn')) return;
    
    const context = this.formatMessage('warn', message, metadata);
    this.logToConsole(context);
  }

  /**
   * Log errors with optional Error object and metadata
   */
  error(message: string, error?: Error | any, metadata?: LogMetadata): void {
    if (!this.shouldLog('error')) return;

    // Handle different error types
    let errorObj: Error | undefined;
    if (error instanceof Error) {
      errorObj = error;
    } else if (error && typeof error === 'object' && error.message) {
      // Convert error-like objects to Error
      errorObj = new Error(error.message);
      errorObj.stack = error.stack;
    } else if (error) {
      // Convert non-Error values to Error
      errorObj = new Error(String(error));
    }

    const context = this.formatMessage('error', message, metadata, errorObj);
    this.logToConsole(context);

    // In production, you could send errors to an error tracking service here
    // this.sendToErrorTracking(context);
  }

  /**
   * Create a logger instance with a specific component context
   */
  withComponent(componentName: string): ComponentLogger {
    return new ComponentLogger(this, componentName);
  }

  /**
   * Performance logging utility
   */
  time(label: string): () => void {
    if (!this.shouldLog('debug')) return () => {};
    
    const startTime = performance.now();
    const startMessage = `⏱️ Timer started: ${label}`;
    this.debug(startMessage);

    return () => {
      const endTime = performance.now();
      const duration = Math.round(endTime - startTime);
      const endMessage = `⏱️ Timer finished: ${label}`;
      this.debug(endMessage, { duration: `${duration}ms` });
    };
  }

  /**
   * Set session ID for tracking user sessions
   */
  setSessionId(sessionId: string): void {
    this.sessionId = sessionId;
  }

  /**
   * Get current session ID
   */
  getSessionId(): string | undefined {
    return this.sessionId;
  }
}

/**
 * Component-specific logger that includes component context
 */
class ComponentLogger {
  constructor(
    private parentLogger: FrontendLogger,
    private componentName: string
  ) {}

  debug(message: string, metadata?: LogMetadata): void {
    this.parentLogger.debug(
      `[${this.componentName}] ${message}`,
      { ...metadata, component: this.componentName }
    );
  }

  info(message: string, metadata?: LogMetadata): void {
    this.parentLogger.info(
      `[${this.componentName}] ${message}`,
      { ...metadata, component: this.componentName }
    );
  }

  warn(message: string, metadata?: LogMetadata): void {
    this.parentLogger.warn(
      `[${this.componentName}] ${message}`,
      { ...metadata, component: this.componentName }
    );
  }

  error(message: string, error?: Error | any, metadata?: LogMetadata): void {
    this.parentLogger.error(
      `[${this.componentName}] ${message}`,
      error,
      { ...metadata, component: this.componentName }
    );
  }

  time(label: string): () => void {
    return this.parentLogger.time(`[${this.componentName}] ${label}`);
  }
}

// Export singleton instance
export const logger = new FrontendLogger();

// Export for testing
export { FrontendLogger, ComponentLogger };

// Convenience re-exports for backward compatibility
export const log = {
  debug: logger.debug.bind(logger),
  info: logger.info.bind(logger),
  warn: logger.warn.bind(logger),
  error: logger.error.bind(logger),
  time: logger.time.bind(logger),
}; 